import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAgentChat } from '../../hooks/useAgentChat';
import { useExcelSelection } from '../../hooks/useExcelSelection';
import { useMode } from '../../hooks/useMode';
import { useModels } from '../../hooks/useModels';
import { useSkills } from '../../hooks/useSkills';
import { FEATURES } from '../../services/features';
import { useUsage } from '../../hooks/useUsage';
import { useUsageAfterIndex } from '../../hooks/useUsageAfterIndex';
import { AccountMenu } from './AccountMenu';
import { useWorkbookContext, useWorkbookInfo } from '../../hooks/useWorkbookContext';
import { apiService } from '../../services/ApiService';
import { excelToolExecutor } from '../../services/excel/ExcelToolExecutor';
import { toolRunner } from '../../services/agent/ToolRunner';
import { conversationLanguage } from '../../services/agent/conversationLanguage';
import { getWorkbookId } from '../../services/excel/workbookIdentity';
import type { AuditFindingView, AuditView, DisplayDocument, FeedbackRating, FeedbackTag, QuestionAnswer, ReportReason, TermsView, ThreadOrigin, ToolCard } from '../../types/agent';
import { ProtectedSheetsDialog } from './ProtectedSheets';
import { Composer } from './Composer';
import { ModelSwitch } from './ModelSwitch';
import { FeedbackPrompt } from './Feedback';
import { FlagIcon, ReportPanel } from './ReportPanel';
import { useFeedbackPrompt } from '../../hooks/useFeedbackPrompt';
import { SelectionChip } from './SelectionChip';
import { Brand, GlyphButton, Header, HistoryIcon, LogoutIcon } from './Header';
import { MessageList } from './MessageList';
import type { BubbleActions } from './MessageBubble';
import { ThreadHistory } from './ThreadHistory';
import { Welcome } from './Welcome';
import { useEmptyTransition } from './emptyTransition';
import { useSelectionOptIn } from '../../hooks/useSelectionOptIn';
import { useRecentThread } from '../../hooks/useRecentThread';
import { useWorkbookIndex } from '../../hooks/useWorkbookIndex';
import { IndexStatusLine, WorkbookDot } from './IndexStatus';
import { WorkbookIntro } from './WorkbookIntro';
import { IndexingView } from './IndexingView';
import { IdentityCard } from './IdentityCard';
import { ReadOnlyBanner, ResumedHeader } from './ThreadScope';
import { INDEX_REQUIRED_CODES, INDEX_UNDERSTANDING_CODE } from '../../types/index';

/** The taskpane once signed in (design 1a/1b/1c): header (brand, mode, history, new chat),
 *  welcome or turn list, context chips + composer; the history screen replaces the whole pane. */
/** Header buttons "protected sheets" (#36) and "converge" (#35) are hidden for the moment (product owner,
 *  2026-09-10); the features stay wired (dialog, auto-convergence after a write) — flip this to bring the
 *  buttons back. */
const SHOW_WORKBOOK_ACTIONS = false;

export function ChatPanel({ firstName, email = null, admin = false, onSignedOut }: { firstName: string | null; email?: string | null; admin?: boolean; onSignedOut: () => void }) {
  const { t } = useTranslation();
  const [workbookId, setWorkbookId] = useState<string | null>(null);
  const [screen, setScreen] = useState<'chat' | 'history'>('chat');
  const [mode, setMode] = useMode(email);
  const models = useModels(apiService, email);
  const skills = useSkills(apiService, FEATURES.skills);
  const { usage, refresh: refreshUsage, setUsage } = useUsage();
  const selection = useExcelSelection();
  const info = useWorkbookInfo(selection);
  // The selection is context once the user selects something after the pane opened (not the cursor as it was), or
  // adds it with the chip; the × removes it; off again on every new / opened thread (useSelectionOptIn).
  const [includeSelection, setIncludeSelection, resetIncludeSelection] = useSelectionOptIn(selection);
  const includeSelectionRef = useRef(includeSelection);
  includeSelectionRef.current = includeSelection;
  const readIncludeSelection = useCallback(() => includeSelectionRef.current, []);
  const workbookIdRef = useRef<string | null>(null);
  workbookIdRef.current = workbookId;
  const readWorkbookId = useCallback(() => workbookIdRef.current, []);
  const { build } = useWorkbookContext(readWorkbookId, setWorkbookId, readIncludeSelection);
  // The workbook index (2026-09-14): captured whole at pane open, the composer locked until it is ready.
  const index = useWorkbookIndex(setWorkbookId);
  const indexRefresh = index.refresh;
  const indexSnapshot = index.snapshot;
  const indexRefreshInBackground = index.refreshInBackground;
  const indexRecheck = index.recheck;
  const indexAwaitUnderstood = index.awaitUnderstood;
  const [protectDialog, setProtectDialog] = useState(false);
  const [convergeNote, setConvergeNote] = useState<string | null>(null);
  const circularBlocks = useCallback(() => (workbookIdRef.current ? apiService.getCircularBlocks(workbookIdRef.current).catch(() => []) : Promise.resolve([])), []);
  const describeConverge = useCallback((block: string, r: Record<string, unknown>): string => {
    if (typeof r.error === 'string') return t('converge.failed', { block, error: r.code === 'IterativeCalculationEnabled' ? t('converge.iterativeOn') : String(r.error) });
    if (r.verdict === 'converged') return t('converge.converged', { block, count: Number(r.iterations) || 0 });
    if (r.verdict === 'diverged') return t('converge.diverged', { block });
    return t('converge.capped', { block, count: Number(r.iterations) || 0 });
  }, [t]);
  const chat = useAgentChat({ buildContext: build, mode, onUsage: setUsage, onThreadChanged: () => { resetIncludeSelection(); void refreshUsage(); }, getWorkbookId: () => workbookIdRef.current, circularBlocks, onConverged: (info) => setConvergeNote(describeConverge(info.block_id, info.result)),
    // A fingerprint does not see every write (formats, styles, recalculated values): tell the index
    // which sheets to capture again. It catches up once the agent stops working, once per turn.
    onSheetsWritten: (sheets) => index.markWritten(sheets),
    onAgentWorking: (working) => index.setAgentWorking(working),
    // A turn refused for a missing or stale index waits for the rebuild and goes again by itself. The chat
    // stays on screen: the rebuild shows in the status line under the header, the waiting turn in its bubble.
    refreshIndex: async () => {
      await indexRefreshInBackground();
      const state = indexSnapshot();
      return state.phase === 'failed' ? t('chat.indexFailed', { error: state.error ?? '' }) : null;
    },
    // A turn refused because the server has not finished understanding the version it serves — the catalog of a
    // version just committed, built while the composer stays open (sculpt-api D28: every version waits for its
    // own) — waits for that pass in its bubble and goes again by itself; nothing is captured.
    awaitUnderstanding: async () => ((await indexAwaitUnderstood()) ? null : t('chat.indexNotUnderstood')),
    // The model of a new conversation; a refused one greys out in the picker once the listing is read again.
    model: models.selected,
    onModelRefused: () => {
      void models.refresh();
      return t('model.refused');
    },
    // 429 before the stream: the user's other workbooks already run as many turns as the server allows at once.
    onTurnsInProgress: (limit) => t('chat.turnsInProgress', { limit }),
    // The model provider cannot serve the turn (the server has told staff): a calm word, no cause.
    onServiceInterrupted: () => t('chat.serviceInterrupted'),
    // An audit card that changed what is fixed (a turn marked fixes, an undo opened them again): the Audit Findings
    // sheet the user wrote follows it, only the Status and Fix cells of the findings that moved.
    onAuditCard: async (view) => {
      const synced = await excelToolExecutor.syncAuditFindingsSheet(view);
      if (!synced?.findings.length) return false;
      index.markWritten([synced.sheet]);
      return true;
    } });
  const { session } = chat;

  // On open: read the workbook id WITHOUT creating it. The pane reopens on the thread it showed if it was
  // closed less than five minutes ago, otherwise on the home screen; the history lists the rest.
  useEffect(() => {
    void getWorkbookId().then((id) => setWorkbookId(id)).catch(() => undefined);
  }, []);
  const loadThread = useCallback((id: string) => session.loadThread(id), [session]);
  const resuming = useRecentThread({ workbookId, ready: index.phase !== 'identity', threadId: chat.threadId, load: loadThread });

  // Whether the open thread may be written from this workbook, and where it was resumed from.
  const [scope, setScope] = useState<{ threadId: string; writable: boolean; workbookName: string | null; origin: ThreadOrigin | null; model: string | null } | null>(null);
  useEffect(() => {
    const threadId = chat.threadId;
    if (!threadId || !workbookId) {
      setScope(null);
      return;
    }
    let cancelled = false;
    apiService.getThread(threadId, workbookId)
      .then((d) => {
        if (!cancelled) setScope({ threadId, writable: d.writable !== false, workbookName: d.workbook_name ?? null, origin: d.origin ?? null, model: d.model ?? null });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [chat.threadId, workbookId]);
  const readOnly = !!scope && scope.threadId === chat.threadId && !scope.writable;
  const [continuing, setContinuing] = useState(false);
  const [continueError, setContinueError] = useState<string | null>(null);
  const continueHere = async () => {
    if (!chat.threadId || !workbookId) return;
    setContinuing(true);
    setContinueError(null);
    try {
      const resumed = await apiService.resumeHere(chat.threadId, workbookId, models.selected);
      await session.loadThread(resumed.thread_id);
    } catch (err) {
      setContinueError(err instanceof Error ? err.message : String(err));
    } finally {
      setContinuing(false);
    }
  };
  const openThread = (id: string) => void session.loadThread(id).catch((err) => console.warn('[agent-v2] open thread failed', err));

  // A Question card is the one wait the user can talk their way out of: the composer stays open and what
  // they type answers the card (PLAN-questions.md D10).
  const busy = (chat.streaming || chat.busy) && !chat.awaitingAnswers;
  // A turn just ended: re-read the usage (the stream's `usage` events miss the index classifier and other members).
  const turnRunning = chat.streaming || chat.busy;
  const wasRunning = useRef(turnRunning);
  useEffect(() => {
    if (wasRunning.current && !turnRunning) void refreshUsage();
    wasRunning.current = turnRunning;
  }, [turnRunning, refreshUsage]);
  // An index version just understood: its model pass billed the user, and no stream event says so.
  useUsageAfterIndex(index.status, refreshUsage);
  const [composerDirty, setComposerDirty] = useState(false);
  // A turn refused for the index (409) is normally waited for and sent again by the session (`refreshIndex` /
  // `awaitUnderstanding` above); this is the fallback for the refusal it reports once its waits are used up:
  // re-index ONCE per such error, the user resends afterwards. The error stays displayed while the re-index runs, so the effect must not fire again on the
  // re-renders the index progress causes (each one started another full re-index, 2026-09-15). A turn refused
  // because the server's pass has not finished is not a rebuild: nothing changed, the pane waits again (`recheck`).
  const handledIndexError = useRef<string | null>(null);
  useEffect(() => {
    const understanding = chat.error?.includes(INDEX_UNDERSTANDING_CODE) ? chat.error : null;
    const indexError = understanding ?? (chat.error && INDEX_REQUIRED_CODES.some((code) => chat.error?.includes(code)) ? chat.error : null);
    if (!indexError) {
      handledIndexError.current = null;
      return;
    }
    if (handledIndexError.current === indexError) return;
    handledIndexError.current = indexError;
    void (understanding ? indexRecheck() : indexRefresh());
  }, [chat.error, indexRefresh, indexRecheck]);
  const feedback = useFeedbackPrompt({
    threadId: chat.threadId,
    messages: chat.messages,
    busy,
    composerDirty,
    mode,
    workbookId,
    send: (body) => apiService.sendFeedback(body),
  });
  const onShow = useCallback((card: ToolCard) => {
    if (card.target) void excelToolExecutor.highlight(card.target.sheet, card.target.address).catch(() => undefined);
  }, []);
  const onUndo = useCallback((card: ToolCard) => void session.undoCard(card.tool_call_id), [session]);
  // A formula the plan left alone: clicking its address takes the user to it, which is where they read it.
  const onShowCell = useCallback((sheet: string, cell: string) => {
    void excelToolExecutor.highlight(sheet, cell).catch(() => undefined);
  }, []);
  // The workbook's sheet names, from the index the pane already waited for. They decide which code span in
  // the agent's prose is a place: without them nothing is linked, which is the right default while the
  // index loads — a link that cannot work should not be offered.
  // The same array for as long as the names are the same: every status the index publishes (a poll, a catch-up after
  // the agent's writes) is a new object, and a new list here parsed the whole thread's markdown again.
  const sheetNames = (index.status?.sheets ?? []).map((s) => s.name).join('\u0000');
  const sheets = useMemo(() => (sheetNames ? sheetNames.split('\u0000') : []), [sheetNames]);
  // One `actions` object for as long as what it offers stays the same: MessageBubble is memoised on it and the
  // markdown setup is cached by it, so a new object on every render re-rendered and re-parsed every turn of the
  // thread on every streamed token. What the callbacks read at call time comes from `live`.
  const live = useRef({ chat, mode, workbookId, t });
  live.current = { chat, mode, workbookId, t };
  const actions = useMemo<BubbleActions>(() => ({
    onDecide: (id: string, ok: boolean, opts?: { overwrite_ack?: boolean; message?: string }) => session.decide(id, ok, opts),
    onDecideAll: (ok: boolean) => session.decideAll(ok),
    onAnswerQuestions: (answers: QuestionAnswer[]) => void session.answerQuestions(answers),
    onQuestionDraft: (answers: QuestionAnswer[]) => session.setQuestionDraft(answers),
    onCancelQuestions: () => void session.cancelQuestions(),
    onShow,
    onShowCell,
    // The Audit Findings sheet comes from the card the pane holds, not from the model: one write, every finding.
    onWriteAuditSheet: readOnly ? undefined : (view: AuditView) => excelToolExecutor.writeAuditFindingsSheet(view),
    // The Term Sheet Gaps sheet likewise: written from the terms card, every term, the gaps first.
    onWriteTermsSheet: readOnly ? undefined : (view: TermsView) => excelToolExecutor.writeTermsSheet(view),
    // "Fix" asks the agent: the write then goes through the turn's approvals, undo and read-back like any other.
    // Sent in the conversation's language, not the UI's, or the agent would switch language on the click.
    onFixFinding: readOnly || busy ? undefined : (f: AuditFindingView) => void session.send(
      live.current.t('audit.fixMessage', { id: f.id, title: f.title, lng: conversationLanguage(live.current.chat.messages) ?? undefined })),
    sheets,
    onUndo,
    onRateMessage: (turnIndex: number, rating: FeedbackRating, tags: FeedbackTag[], comment: string) => {
      const { chat: c, mode: m, workbookId: wb } = live.current;
      if (!c.threadId) return;
      // Kept on the turn first: the thumbs and the comment form show what was sent, not what a component remembers.
      session.noteRating(turnIndex, { turn_index: turnIndex, rating, tags, comment: comment.trim() || null });
      // Fire and forget: a lost rating is not worth interrupting the user over, and the row is an upsert
      // so the follow-up with chips merges into whatever the thumb already wrote.
      void apiService
        .sendFeedback({ thread_id: c.threadId, turn_index: turnIndex, outcome: 'rated', rating, tags,
                        comment: comment.trim() || null, source: 'message', mode: m, workbook_id: wb })
        .catch(() => undefined);
    },
    onRetractMessage: (turnIndex: number) => {
      const threadId = live.current.chat.threadId;
      if (!threadId) return;
      session.noteRating(turnIndex, null);
      void apiService.clearFeedback(threadId, turnIndex).catch(() => undefined);
    },
  }), [session, onShow, onShowCell, onUndo, sheets, readOnly, busy]);
  const send = (text: string, documents: DisplayDocument[] = []) => void session.send(text, documents);
  const empty = chat.messages.length === 0;
  const composerRef = useRef<HTMLDivElement>(null);
  const hero = useEmptyTransition(empty, composerRef);
  const convergeAll = async () => {
    const blocks = await circularBlocks();
    if (!blocks.length) {
      setConvergeNote(t('converge.none'));
      return;
    }
    setConvergeNote(t('converge.running'));
    const notes: string[] = [];
    for (const b of blocks) {
      const exec = await toolRunner.run({ tool_call_id: `local-${Date.now()}`, tool_name: 'excel_converge', input: { sheet: b.sheet, live_range: b.live_range, circ_range: b.circ_range, switch_cell: b.switch_cell, delta_cell: b.delta_cell, tolerance: b.tolerance, max_iterations: b.max_iterations }, preview: '', risk: 'write', requires_confirmation: false });
      notes.push(describeConverge(b.block_id, exec.result));
    }
    setConvergeNote(notes.join(' · '));
  };
  const newThread = () => {
    setScreen('chat');
    session.newThread();
  };
  // A conversation keeps the model of its first turn (PLAN-model-choice.md D1): the picker shows it, and picking
  // another one starts a new conversation on it. Until the thread's detail is read, the pick it was sent with.
  const threadModel = chat.threadId && scope?.threadId === chat.threadId ? scope.model : null;
  // The report panel belongs to the thread it was opened on: switching or starting a thread closes it.
  const [reportingThread, setReportingThread] = useState<string | null>(null);
  const reporting = !!chat.threadId && reportingThread === chat.threadId;
  const closeReport = useCallback(() => setReportingThread(null), []);
  const report = async (reason: ReportReason, comment: string) => {
    if (!chat.threadId) return;
    await apiService.reportConversation({ thread_id: chat.threadId, reason, comment: comment || null });
  };
  const pickModel = (id: string) => {
    models.choose(id);
    if (chat.threadId) newThread();
  };
  const signOut = async () => {
    await session.stop();
    await apiService.logout();
    onSignedOut();
  };

  if (screen === 'history') {
    return (
      <ThreadHistory
        workbookId={workbookId}
        workbookName={info.name}
        currentThreadId={chat.threadId}
        onClose={() => setScreen('chat')}
        onNew={newThread}
        // Leave the history once the thread is loaded: switching first showed the home screen while it loaded.
        onOpen={(id) => session.loadThread(id)
          .then(() => setScreen('chat'))
          .catch((err) => console.warn('[agent-v2] open thread failed', err))}
        onSignOut={() => void signOut()}
      />
    );
  }

  // Before the chat: a workbook the server has never seen is explained and offered, not indexed behind the
  // user's back, and its first index takes the pane rather than hiding under a locked composer. Everything
  // after that — a reopen, the catch-up after a write — happens in the status line with the chat open.
  // The identity question takes the pane too: nothing is read and the chat waits until it is answered (D6).
  // While the pane is still asking the server what it knows (or has not asked yet), show neither the intro
  // nor the chat, and no spinner either: the probe takes a fraction of a second, and anything shown during
  // it (the consent screen at first, then a spinner) flashed before the chat opened. Same while the thread
  // shown before a recent close is reopened: the home screen flashed before it.
  const firstIndex = index.phase === 'identity' && index.identity
    ? 'identity'
    : index.phase === 'probing' || (index.phase === 'idle' && !index.ready)
    ? 'probing'
    : index.phase === 'cell_edit'
    ? 'cellEdit'
    : index.phase === 'needs_consent'
    ? 'intro'
    : !index.ready && (index.phase === 'capturing' || index.phase === 'committing' || index.phase === 'understanding' || index.phase === 'failed')
      ? 'indexing'
      : resuming
      ? 'probing'
      : null;
  if (firstIndex) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-pane">
        <Header left={<Brand title={t('app.title')} />} right={<><AccountMenu admin={admin} email={email} /><GlyphButton glyph={<LogoutIcon />} title={t('header.logout')} onClick={() => void signOut()} /></>} />
        {firstIndex === 'identity' && index.identity
          ? <IdentityCard question={index.identity} error={index.error} onAnswer={(a) => void index.answerIdentity(a)} />
          : firstIndex === 'probing'
          ? <div className="flex-1" data-testid="workbook-probing" />
          : firstIndex === 'cellEdit'
          ? <div className="flex flex-1 items-center justify-center px-5 text-center text-[13px] text-muted" data-testid="workbook-cell-edit" role="status">{t('intro.cellEdit')}</div>
          : firstIndex === 'intro'
          ? <WorkbookIntro
              index={index}
              models={models.models}
              selected={models.selected}
              onStart={(model) => {
                if (model) models.choose(model);
                void index.consent(model);
              }}
            />
          : <IndexingView index={index} onRetry={() => void index.refresh()} />}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-pane">
      <Header
        left={<><Brand title={t('app.title')} /><WorkbookDot index={index} onReread={() => void index.reread()} /></>}
        right={
          <>
            <GlyphButton glyph={<HistoryIcon />} title={t('header.history')} onClick={() => setScreen('history')} />
            {chat.threadId && (
              <GlyphButton glyph={<FlagIcon />} title={t('header.report')} onClick={() => setReportingThread(reporting ? null : chat.threadId)} active={reporting} />
            )}
            {SHOW_WORKBOOK_ACTIONS && (
              <>
                <GlyphButton glyph="🔒" title={t('header.protect')} onClick={() => setProtectDialog(true)} size={12} />
                <GlyphButton glyph="↻" title={t('header.converge')} onClick={() => void convergeAll()} size={15} />
              </>
            )}
            <GlyphButton glyph="+" title={t('header.newChat')} onClick={newThread} size={16} />
            <AccountMenu admin={admin} email={email} />
            <GlyphButton glyph={<LogoutIcon />} title={t('header.logout')} onClick={() => void signOut()} />
          </>
        }
      />
      <IndexStatusLine index={index} onRetry={() => void indexRefresh()} />
      {scope?.origin && scope.threadId === chat.threadId && <ResumedHeader origin={scope.origin} onOpen={openThread} />}
      {readOnly && <ReadOnlyBanner workbookName={scope?.workbookName ?? null} continuing={continuing} error={continueError} onContinue={() => void continueHere()} />}
      {protectDialog && <ProtectedSheetsDialog workbookId={workbookId} onClose={() => setProtectDialog(false)} />}
      {convergeNote && (
        <div className="flex items-center gap-2 border-b border-line-soft bg-hover-soft px-3.5 py-1.5 text-[12px] text-ink">
          <span className="min-w-0 flex-1 truncate">{convergeNote}</span>
          <button type="button" className="text-muted" onClick={() => setConvergeNote(null)} aria-label={t('converge.dismiss')}>×</button>
        </div>
      )}
      {/* Empty thread: the welcome hero and the composer sit together in the middle of the pane; from the first
          message on, the conversation takes the space and the composer docks at the bottom. One Composer element in
          a stable slot, so its draft and attachments survive the switch. */}
      <div className={`relative flex min-h-0 flex-1 flex-col ${empty ? 'justify-center gap-6 pb-10' : ''}`}>
        {empty ? <Welcome firstName={firstName} mode={mode} /> : <MessageList messages={chat.messages} actions={actions} />}
        {!empty && hero === 'leaving' && (
          <div className="pointer-events-none absolute inset-x-0 top-[22%] animate-pc-out" aria-hidden="true">
            <Welcome firstName={firstName} mode={mode} />
          </div>
        )}
        {chat.error && !chat.messages.some((m) => m.error) && <p className="px-3.5 py-1 text-[12px] text-danger">{chat.error}</p>}
        {/* Directly above the composer. It needs several settled turns, so it can never appear over the
            centred empty-thread layout. The report panel, opened from the header, takes its place meanwhile. */}
        {reporting && <ReportPanel key={chat.threadId} onSubmit={report} onClose={closeReport} />}
        {feedback.visible && !reporting && (
          <FeedbackPrompt onSubmit={feedback.submit} onSkip={feedback.skip} onDone={feedback.close} />
        )}
        <Composer
          placement={empty ? 'center' : 'bottom'}
          outerRef={composerRef}
        mode={mode}
        onModeChange={setMode}
        modelSwitch={<ModelSwitch models={models.models} value={threadModel ?? models.selected} onChange={pickModel} started={!!chat.threadId} disabled={busy || readOnly} />}
        skills={skills}
        chips={<SelectionChip selection={selection} included={includeSelection} onToggle={setIncludeSelection} />}
        disabled={!!usage?.limit_reached || !index.ready || readOnly}
        lockedHint={usage?.limit_reached ? null : readOnly ? t('thread.composerReadOnly') : index.phase === 'failed' ? t('index.composerFailed') : index.phase === 'understanding' ? t('index.composerUnderstanding') : t('index.composerLocked')}
        busy={busy}
        answering={chat.awaitingAnswers}
        usage={usage}
        admin={admin}
        onSend={send}
        onStop={() => void session.stop()}
        api={apiService}
        threadId={chat.threadId}
          onDraftChange={setComposerDirty}
        />
      </div>
    </div>
  );
}
